import { useState, useCallback, useEffect, useRef } from 'react';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { DEFAULT_UPLOAD_FOLDER, handleFileUpload, removeFileFromFirebase } from '../../../../helpers/common';
import { dispatchTaskError } from '../../../../services/taskServices';
import { captureException } from '../../../../services/logService';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';

export const useCommentFiles = (
  externalUploadedFiles: CommentFilesInterface[] | undefined,
  externalSetUploadedFiles:
    | ((files: CommentFilesInterface[] | ((prevFiles: CommentFilesInterface[]) => CommentFilesInterface[])) => void)
    | undefined,
  editorRef: React.RefObject<TiptapEditorRef>,
  taskId?: string,
  uploadFolder: string = DEFAULT_UPLOAD_FOLDER
) => {
  const [uploadedFiles, setUploadedFilesLocal] = useState<CommentFilesInterface[]>(externalUploadedFiles || []);
  const [loading, setLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync external file list to local state (legacy mode only).
  // In managed mode  local files are loaded from the draft cache.
  // Skip this sync to prevent racing against the draft load and overwriting local user additions.
  useEffect(() => {
    if (externalUploadedFiles !== undefined) {
      setUploadedFilesLocal(externalUploadedFiles);
    }
  }, [externalUploadedFiles]);

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files?.length === 0) {
        dispatchTaskError('No files selected.');
        return;
      }
      try {
        setLoading(true);
        const fileArray = Array.from(files); // Convert FileList to Array
        const updatedFiles = (await handleFileUpload(
          uploadedFiles || [],
          fileArray,
          editorRef,
          undefined,
          uploadFolder
        )) as CommentFilesInterface[];
        setUploadedFilesLocal((previousFiles: CommentFilesInterface[]) => [
          ...(previousFiles || []),
          ...(updatedFiles || [])
        ]);
        if (externalSetUploadedFiles) {
          externalSetUploadedFiles((previousFiles: CommentFilesInterface[]) => [
            ...(previousFiles || []),
            ...(updatedFiles || [])
          ]);
        }
      } catch (error) {
        captureException(error);
        dispatchTaskError('Error occurred while uploading files.');
      } finally {
        setLoading(false);
      }
    },
    [externalSetUploadedFiles, uploadedFiles, editorRef, uploadFolder]
  );

  const onClickClose = useCallback(
    (index: number, file: CommentFilesInterface) => {
      if (file && !file.uploadFileType && file.href.startsWith('https://firebasestorage.googleapis'))
        removeFileFromFirebase(file.href);
      const updateFiles = (previousFiles: CommentFilesInterface[]) => {
        const filesClone = JSON.parse(JSON.stringify(previousFiles || []));
        filesClone.splice(index, 1);
        return filesClone;
      };
      setUploadedFilesLocal((previousFiles: CommentFilesInterface[]) => updateFiles(previousFiles));
      if (externalSetUploadedFiles) {
        externalSetUploadedFiles((previousFiles: CommentFilesInterface[]) => updateFiles(previousFiles));
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [externalSetUploadedFiles]
  );

  const handleFileInput = useCallback(() => {
    queueMicrotask(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    });
  }, []);

  const addDroppedFiles = useCallback(
    (files: CommentFilesInterface[]) => {
      setUploadedFilesLocal((previousFiles: CommentFilesInterface[]) => [...(previousFiles || []), ...(files || [])]);
      if (externalSetUploadedFiles) {
        externalSetUploadedFiles((previousFiles: CommentFilesInterface[]) => [
          ...(previousFiles || []),
          ...(files || [])
        ]);
      }
    },
    [externalSetUploadedFiles]
  );

  return {
    uploadedFiles,
    setUploadedFilesLocal,
    loading,
    setLoading,
    fileInputRef,
    onFileUpload,
    onClickClose,
    handleFileInput,
    addDroppedFiles
  };
};
